import { AddressValidatorComponent } from './../../shared/address-validator/address-validator.component';
import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { Code } from 'src/app/core/model/dictionary';
import { HelperService } from 'src/app/core/service/helper.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { FacilityValidators } from 'src/app/core/validators/facility.validators';
import { FacilityEdit } from '../model/facility-edit';

@Component({
  standalone: false,
  selector: 'app-facility-edit',
  templateUrl: './facility-edit.component.html',
  styleUrls: ['./facility-edit.component.scss']
})
export class FacilityEditComponent implements OnInit, AfterViewChecked
{
  @ViewChild('addressValidator') public addressValidator: AddressValidatorComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<FacilityEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public addressChanged = false;
  public addressAPIEnabled = false;
  public saving = false;
  public facilityToSave: any;

  public states: Code[] = [];
  public countries: Code[] = [];
  public phoneNumberMask: string;

  public urlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  private facilityEdit: FacilityEdit;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private helperService: HelperService,
    private fb: UntypedFormBuilder,
    private facilityValidators: FacilityValidators,
    private settingService: SettingService)
  {
    this.loadLookups();
  }

  public get longitude(): UntypedFormControl
  {
    return this.editForm.get('longitude');
  }

  public get latitude(): UntypedFormControl
  {
    return this.editForm.get('latitude');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get npi(): UntypedFormControl
  {
    return this.editForm.get('npi');
  }

  public get url(): UntypedFormControl
  {
    return this.editForm.get('url');
  }

  public get streetLine(): UntypedFormControl
  {
    return this.editForm.get('streetLine');
  }

  public get streetLineAdditional(): UntypedFormControl
  {
    return this.editForm.get('streetLineAdditional');
  }

  public get city(): UntypedFormControl
  {
    return this.editForm.get('city');
  }

  public get county(): UntypedFormControl
  {
    return this.editForm.get('county');
  }

  public get postalCode(): UntypedFormControl
  {
    return this.editForm.get('postalCode');
  }

  public get state(): UntypedFormControl
  {
    return this.editForm.get('state');
  }

  public get country(): UntypedFormControl
  {
    return this.editForm.get('country');
  }

  public get cellPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('cellPhoneNumber');
  }

  public get faxNumber(): UntypedFormControl
  {
    return this.editForm.get('faxNumber');
  }

  public get telephoneNumber(): UntypedFormControl
  {
    return this.editForm.get('telephoneNumber');
  }

  public get telephoneNumberExtension(): UntypedFormControl
  {
    return this.editForm.get('telephoneNumberExtension');
  }

  @Input() public set model(facilityEdit: FacilityEdit)
  {
    this.initModel(facilityEdit);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.addressAPIEnabled = sessionSetting.addressAPIEnabled;
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const objectToPersist = this.editForm.value;

    // Set address object and clean up
    this.setAddress(objectToPersist);

    this.performFacilitySave(objectToPersist);
  }

  /**
   * Perform the Facility Save.
   */
  public saveFacility(): void
  {
    // Emit save event
    this.save.emit(this.facilityToSave);
    this.addressChanged = false;
    this.saving = false;
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Address change event from Address Validator component.
   *
   * @param event Address data being selected to apply.
   */
  public changeAddress(event: any): void
  {
    // Override the address based on Address API response.
    if (event.confirmation === 'recommended')
    {
      // Force dirty to change state
      this.editForm.markAsDirty();

      // Set form properties
      this.streetLine.setValue(event.line1);
      this.streetLineAdditional.setValue(event.line2);
      this.state.setValue(event.postalAddress && event.postalAddress.administrativeArea ?
        this.states.find(x => x.code === event.postalAddress.administrativeArea) : null);
      this.city.setValue(event.postalAddress && event.postalAddress.locality ? event.postalAddress.locality : null);
      this.postalCode.setValue(event.postalAddress && event.postalAddress.postalCode ? event.postalAddress.postalCode : null);
      this.latitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.latitude : null);
      this.longitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.longitude : null);

      if (event.postalAddress && event.postalAddress.regionCode)
      {
        this.country.setValue(this.countries.find(x => x.alternateCode === event.postalAddress.regionCode));
      }
      this.addressChanged = false;    // Mark address unchanged to avoid opening modal again unless it is manually changed again.
    }
    else
    {
      this.latitude.setValue(null);
      this.longitude.setValue(null);
    }

    // If autoSave, then set patient object and save.
    if (event.autoSave)
    {
      this.setAddress(this.facilityToSave);
      this.saveFacility();
    }
  }

  /**
   * Manual Address Validation. No autoSave will be performed.
   */
  public validateAddress(): void
  {
    this.addressValidator.execute(false, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
      this.state.value ? this.state.value.code : null, this.country.value ? this.country.value.code : null, this.postalCode.value);
  }

  public isValidUrl(): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return this.url.value !== undefined && this.url.value && regEx.test(this.url.value);
  }

  public openUrl(): void
  {
    if (this.isValidUrl())
    {
      window.open(this.url.value);
    }
  }

  private setAddress(objectToPersist: any): void
  {
    // Init object if null
    if (objectToPersist.address === null || objectToPersist.address === undefined)
    {
      objectToPersist.address = {};
    }

    objectToPersist.address.streetLine = this.streetLine && this.streetLine.value !== '' ? this.streetLine.value : null;
    objectToPersist.address.streetLineAdditional = this.streetLineAdditional && this.streetLineAdditional.value !== '' ?
      this.streetLineAdditional.value : null;
    objectToPersist.address.city = this.city && this.city.value !== '' ? this.city.value : null;
    objectToPersist.address.county = this.county && this.county.value !== '' ? this.county.value : null;
    objectToPersist.address.postalCode = this.postalCode && this.postalCode.value !== '' ? this.postalCode.value : null;
    objectToPersist.address.state = this.state && this.state.value ? this.state.value : null;
    objectToPersist.address.country = this.country && this.country.value ? this.country.value : null;
    objectToPersist.address.longitude = this.longitude && this.longitude.value !== '' ? this.longitude.value : null;
    objectToPersist.address.latitude = this.latitude && this.latitude.value !== '' ? this.latitude.value : null;

    delete objectToPersist.streetLine;
    delete objectToPersist.streetLineAdditional;
    delete objectToPersist.city;
    delete objectToPersist.county;
    delete objectToPersist.postalCode;
    delete objectToPersist.state;
    delete objectToPersist.country;
    delete objectToPersist.latitude;
    delete objectToPersist.longitude;
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private loadLookups(): void
  {
    this.helperService.getStatesCountriesFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.states = responseList[0];
        this.countries = responseList[1];
      }
    });
  }

  private initModel(facilityEdit: FacilityEdit)
  {
    if (facilityEdit === undefined)
    {
      return;
    }

    // Create temporary address properties to make a flat properties for the address.
    // This is necessary to not losing values when reseting the form state below.
    facilityEdit.streetLine = facilityEdit.address ? facilityEdit.address.streetLine : null;
    facilityEdit.streetLineAdditional = facilityEdit.address ?
      facilityEdit.address.streetLineAdditional : null;
    facilityEdit.city = facilityEdit.address ? facilityEdit.address.city : null;
    facilityEdit.county = facilityEdit.address ? facilityEdit.address.county : null;
    facilityEdit.postalCode = facilityEdit.address ? facilityEdit.address.postalCode : null;
    facilityEdit.state = facilityEdit.address ? facilityEdit.address.state : null;
    facilityEdit.country = facilityEdit.address ? facilityEdit.address.country : null;

    // Set the current name used for name async validators
    const previousName = this.isNew ? '' : facilityEdit.name;
    const urlValidators = [
      Validators.pattern(this.urlPattern)
    ];

    // Set form
    this.editForm = this.fb.group({
      id: facilityEdit.id,
      version: facilityEdit.version,
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.facilityValidators.validateFacilityNameUnique(this.editForm.get('name'), previousName)
        },
      ],
      description: facilityEdit.description,
      npi: facilityEdit.npi,
      url: [
        { value: facilityEdit.url, disabled: false },
        {
          validators: urlValidators
        }
      ],
      telephoneNumber: facilityEdit.telephoneNumber,
      telephoneNumberExtension: facilityEdit.telephoneNumberExtension,
      cellPhoneNumber: facilityEdit.cellPhoneNumber,
      faxNumber: facilityEdit.faxNumber,
      address: facilityEdit.address,
      // Temporary properties below
      streetLine: facilityEdit.streetLine,
      streetLineAdditional: facilityEdit.streetLineAdditional,
      city: facilityEdit.city,
      county: facilityEdit.county,
      postalCode: facilityEdit.postalCode,
      state: facilityEdit.state,
      country: facilityEdit.country,
      longitude: null,
      latitude: null,
    });

    this.editForm.reset(facilityEdit);
    this.name.setValue(facilityEdit.name);  // This is a work around to fix asyncValidators

    // Monitor address changes to not show address validator all the time.
    this.addressChanged = false;
    this.editForm.get('streetLine').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('streetLineAdditional').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('city').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('state').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('country').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('postalCode').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });

    this.facilityEdit = facilityEdit;
    this.editFormActive = true;
  }

  private performFacilitySave(facility: any): void
  {
    this.saving = true;
    this.facilityToSave = facility;
    if (this.addressChanged && this.streetLine.value)
    {
      this.addressValidator.execute(true, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
        this.state.value ? this.state.value.code : null, this.country.value ? this.country.value.code : null, this.postalCode.value);
    }
    else
    {
      this.saveFacility();
    }
  }
}
